import { MeshBuilder, type Mesh } from "@babylonjs/core";
import { Btn, HeroId, MoveKind, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import { bell, click, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import * as loops from "../../audio/cues/loops.ts";
import { glow } from "../../game/materials.ts";
import type { AvatarClip, ClientHero, ProjectileFrame, ZoneFrame } from "../types.ts";

const PK_SPIT = pk(HeroId.Geoff, 0);
const PK_BURST = pk(HeroId.Geoff, 1);
const ZK_BUBBLE = zk(HeroId.Geoff, 0);
const ZK_POOL = zk(HeroId.Geoff, 1);
const ZK_BUBBLE_BUFF = zk(HeroId.Geoff, 2);
const FX_WATER = fxk(HeroId.Geoff, 0);

type Gear = {
  body: Mesh;
  head: Mesh;
  snout: Mesh;
  dorsal: Mesh;
  tail: Mesh;
  tailFins: Mesh[];
  smile: Mesh;
  teeth: Mesh[];
  belly: Mesh;
  eyes: Mesh[];
  pectorals: Mesh[];
};

const chirp = (base = 860): Cue => trim(6, (o) => {
  tone(o, { type: "triangle", f: base, f1: base * 1.8, glide: 0.07, gain: 0.18, d: 0.1 });
  bell(o, base * 2, 0.08, 0.35);
  return 0.22;
});
const spritzCue: Cue = trim(14, (o) => {
  click(o, 0.12, 3600, 0.003);
  tone(o, { type: "triangle", f: 760, f1: 1180, glide: 0.06, gain: 0.14, d: 0.09 });
  noise(o, { color: "white", type: "bandpass", f: 2600, q: 1.2, gain: 0.18, d: 0.08 });
  return 0.22;
});
const belchCue: Cue = trim(6, (o) => {
  thump(o, 0.35, 210, 70, 0.16);
  whoosh(o, 0.4, 700, 1800, 0.18);
  tone(o, { type: "triangle", f: 480, f1: 760, glide: 0.08, gain: 0.1, d: 0.12 });
  return 0.38;
});
const diveCue: Cue = trim(6.5, (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 1500, f1: 240, gain: 0.55, a: 0.02, d: 0.32 });
  chirp(640)(o);
  return 0.55;
});
const bubbleCue: Cue = trim(16.5, (o) => {
  for (let i = 0; i < 4; i++) tone(o, { type: "sine", f: 520 + i * 180, f1: 900 + i * 180, glide: 0.1, gain: 0.06, d: 0.12, delay: i * 0.045 });
  return 0.45;
});
const chompCue: Cue = trim(0, (o) => {
  thump(o, 0.55, 120, 45, 0.22);
  chirp(520)(o);
  noise(o, { color: "brown", type: "lowpass", f: 700, gain: 0.42, d: 0.35 });
  return 0.75;
});

const hero: ClientHero<Gear> = {
  id: HeroId.Geoff,
  bulk: 1.35,
  shape: { head: 1.45, torso: 0.8, arms: 0.22, legs: 0.18 },
  gear(g) {
    const { scene, rig, add, glow, toon } = g;
    const body = add(MeshBuilder.CreateSphere("geoff-shark-body", { diameter: 1, segments: 20 }, scene), rig.torso, toon("#5fa8d3"));
    body.scaling.set(0.9, 0.58, 1.35);
    body.position.set(0, -0.16, 0.08);
    const head = add(MeshBuilder.CreateSphere("geoff-rounded-head", { diameter: 0.78, segments: 18 }, scene), rig.torso, toon("#6fb9dc"));
    head.scaling.set(0.92, 0.72, 1.05);
    head.position.set(0, 0.1, -0.5);
    const snout = add(MeshBuilder.CreateSphere("geoff-snout", { diameter: 0.52, segments: 14 }, scene), rig.torso, toon("#86cbe9"));
    snout.scaling.set(1.2, 0.62, 1.1);
    snout.position.set(0, 0.02, -0.92);
    const dorsal = add(MeshBuilder.CreateCylinder("geoff-dorsal-fin", { diameterTop: 0, diameterBottom: 0.46, height: 0.72, tessellation: 3 }, scene), rig.torso, toon("#246f96"));
    dorsal.rotation.y = Math.PI / 6;
    dorsal.position.set(0, 0.48, 0.03);
    const tail = add(MeshBuilder.CreateCylinder("geoff-tail-stem", { diameterTop: 0.12, diameterBottom: 0.24, height: 0.62, tessellation: 10 }, scene), rig.torso, toon("#5fa8d3"));
    tail.rotation.x = Math.PI / 2;
    tail.position.set(0, -0.08, 0.86);
    const tailFins = [-1, 1].map((side) => {
      const fin = add(MeshBuilder.CreateCylinder("geoff-tail-fin", { diameterTop: 0, diameterBottom: 0.42, height: 0.48, tessellation: 3 }, scene), rig.torso, toon("#2f79a0"));
      fin.rotation.z = side > 0 ? 0 : Math.PI;
      fin.position.set(0, -0.03 + side * 0.22, 1.12);
      fin.scaling.x = 0.45;
      return fin;
    });
    const smile = add(MeshBuilder.CreateBox("geoff-smile", { width: 0.46, height: 0.045, depth: 0.035 }, scene), rig.torso, glow("#ffffff"));
    smile.position.set(0, -0.12, -1.22);
    const teeth = [-0.16, 0, 0.16].map((x) => {
      const tooth = add(MeshBuilder.CreateCylinder("geoff-tooth", { diameterTop: 0, diameterBottom: 0.055, height: 0.1, tessellation: 3 }, scene), rig.torso, glow("#ffffff"));
      tooth.rotation.z = Math.PI;
      tooth.position.set(x, -0.18, -1.24);
      return tooth;
    });
    const belly = add(MeshBuilder.CreateSphere("geoff-pale-belly", { diameter: 0.78, segments: 16 }, scene), rig.torso, toon("#dff6ff"));
    belly.scaling.set(0.82, 0.26, 1.05);
    belly.position.set(0, -0.42, -0.18);
    const pectorals = [-1, 1].map((side) => {
      const fin = add(MeshBuilder.CreateCylinder("geoff-pectoral-fin", { diameterTop: 0, diameterBottom: 0.36, height: 0.46, tessellation: 3 }, scene), rig.torso, toon("#2f79a0"));
      fin.rotation.z = side * 0.95;
      fin.rotation.x = -0.35;
      fin.position.set(side * 0.52, -0.17, -0.28);
      return fin;
    });
    const eyes = [-1, 1].map((side) => {
      const eye = add(MeshBuilder.CreateSphere("geoff-happy-eye", { diameter: 0.14, segments: 10 }, scene), rig.torso, glow("#0d3b66"));
      eye.scaling.y = 1.25;
      eye.position.set(side * 0.2, 0.13, -1.18);
      return eye;
    });
    return { body, head, snout, dorsal, tail, tailFins, smile, teeth, belly, eyes, pectorals };
  },
  animate(gear, f, rig): AvatarClip {
    const burrow = f.moveKind === MoveKind.Hero;
    for (const p of Object.values(rig.parts)) if (p) p.isVisible = false;
    const bob = Math.sin(f.t * (f.speed > 1 ? 9 : 4.5)) * (f.speed > 1 ? 0.055 : 0.025);
    const chomp = f.action === 5 || f.v[2] > 0;
    for (const mesh of [gear.body, gear.head, gear.snout, gear.tail, gear.belly, gear.smile, ...gear.teeth, ...gear.eyes, ...gear.pectorals, ...gear.tailFins]) mesh.isVisible = !burrow;
    gear.dorsal.isVisible = true;
    gear.body.position.y = -0.16 + bob;
    gear.head.position.y = 0.1 + bob * 0.8;
    gear.snout.position.y = 0.02 + bob * 0.75;
    gear.belly.position.y = -0.42 + bob * 0.65;
    gear.dorsal.position.y = burrow ? -0.1 + Math.sin(f.t * 9) * 0.02 : 0.48 + bob;
    gear.dorsal.position.z = burrow ? -0.08 : 0.03;
    gear.dorsal.scaling.y = burrow ? 1.35 : 1;
    gear.smile.scaling.y = chomp ? 5.5 : 1;
    gear.smile.scaling.x = chomp ? 1.35 : 1;
    gear.tail.rotation.y = Math.sin(f.t * (f.speed > 1 ? 12 : 7)) * 0.35;
    for (const fin of gear.tailFins) fin.rotation.y = gear.tail.rotation.y;
    for (const [i, fin] of gear.pectorals.entries()) fin.rotation.x = -0.35 + Math.sin(f.t * 8 + i) * 0.18;
    return burrow ? "sit" : f.speed > 1 ? "walk" : "idle";
  },
  hud(c, eid) {
    return [
      { label: "Dive", value: hv(c, eid, 0) / 120, color: "#65dbff" },
      { label: "Belly", value: hv(c, eid, 2), color: "#b8f4ff" },
    ];
  },
  lifting(c, eid, buttons) {
    return !!(buttons & Btn.Ability1) && hv(c, eid, 0) > 0;
  },
  projectiles: {
    [PK_SPIT](scene, f: ProjectileFrame) {
      const drop = MeshBuilder.CreateSphere("happy-spit", { diameter: 0.25, segments: 8 }, scene);
      const update = (_: unknown, p: ProjectileFrame) => drop.position.set(p.pos.x, p.pos.y, p.pos.z);
      update(null, f);
      return { gear: drop, update, dispose: () => drop.dispose() };
    },
    [PK_BURST](scene, f: ProjectileFrame) {
      const ball = MeshBuilder.CreateSphere("aqua-belch", { diameter: 0.42, segments: 12 }, scene);
      const update = (_: unknown, p: ProjectileFrame) => ball.position.set(p.pos.x, p.pos.y, p.pos.z);
      update(null, f);
      return { gear: ball, update, dispose: () => ball.dispose() };
    },
  },
  zones: {
    [ZK_BUBBLE](scene, f: ZoneFrame) {
      const bubble = MeshBuilder.CreateSphere("friendly-bubble", { diameter: 0.72, segments: 16 }, scene);
      bubble.material = glow(scene, "#8be8ff", 0.35);
      const update = (_: unknown, z: ZoneFrame) => {
        bubble.position.set(z.pos.x, z.pos.y + 0.42 + Math.sin(z.age * 4) * 0.04, z.pos.z);
        bubble.scaling.setAll(1 + Math.sin(z.age * 5) * 0.05);
      };
      update(null, f);
      return { gear: bubble, update, dispose: () => bubble.dispose() };
    },
    [ZK_BUBBLE_BUFF](scene, f: ZoneFrame) {
      const mote = MeshBuilder.CreateSphere("friendly-bubble-buff", { diameter: 0.2, segments: 8 }, scene);
      mote.material = glow(scene, "#c9fbff", 0.18);
      const update = (_: unknown, z: ZoneFrame) => {
        mote.position.set(z.pos.x, z.pos.y + 1.0 + Math.sin(z.age * 7) * 0.08, z.pos.z);
        mote.scaling.setAll(0.8 + Math.sin(z.age * 9) * 0.12);
      };
      update(null, f);
      return { gear: mote, update, dispose: () => mote.dispose() };
    },
    [ZK_POOL](scene, f: ZoneFrame) {
      const pool = MeshBuilder.CreateCylinder("splash-pool", { diameter: 2, height: 0.04, tessellation: 32 }, scene);
      pool.material = glow(scene, "#65dbff", 0.22);
      const update = (_: unknown, z: ZoneFrame) => {
        pool.position.set(z.pos.x, z.pos.y + 0.02, z.pos.z);
        pool.scaling.set(z.radius, 1, z.radius);
      };
      update(null, f);
      return { gear: pool, update, dispose: () => pool.dispose() };
    },
  },
  fx: { [FX_WATER]: { keepGeneric: true } },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:geoff:belch" : "fire:geoff:spritz", cue: ctx?.alt ? belchCue : spritzCue }),
    ability: {
      [Slot.Ability1]: diveCue,
      [Slot.Ability2]: bubbleCue,
      [Slot.Ultimate]: chompCue,
    },
    theme: { bpm: 132, octave: 1, voice: mallet, notes: [[0, 0.25], [4, 0.25], [7, 0.25], [12, 0.75]] },
    themeTrim: { heroic: 2, hostile: -6, tease: 3 },
    loops: ({ v, alive }) => (alive && v[2] > 0 ? [{ key: "belly", build: loops.portal, gain: 0.35, value: v[2] }] : []),
    selfLift: ({ alive }) => (alive ? loops.flight("orb") : undefined),
    zoneLoops: { [ZK_BUBBLE]: { build: loops.beacon, gain: 0.12, range: 12 }, [ZK_POOL]: { build: loops.portal, gain: 0.25, range: 30 } },
  },
  icons: {
    [Slot.Primary]: "M5 13c4-4 10-4 14 0M7 15h10",
    [Slot.Secondary]: "M6 16c3-7 9-7 12 0M9 17h6",
    [Slot.Ability1]: "M4 15c5-7 11-7 16 0M12 6v9",
    [Slot.Ability2]: "M8 12a4 4 0 108 0a4 4 0 10-8 0",
    [Slot.Ultimate]: "M4 10c4-6 12-6 16 0c-2 8-14 8-16 0z",
    passive: "M6 15c4 4 8 4 12 0",
  },
};

export default hero;
